<template>
  <section
    class="investment-page"
    tabindex="0"
    aria-roledescription="carousel"
    aria-label="招商合作内容"
    @wheel.prevent="onWheel"
    @keydown.left.prevent="prevPage"
    @keydown.right.prevent="nextPage"
    @keydown.home.prevent="goTo(0)"
    @keydown.end.prevent="goTo(images.length - 1)"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
  >
    <h1 class="sr-only">招商合作</h1>
    <div class="carousel-track" :style="trackStyle" aria-live="polite">
      <article
        v-for="(item, index) in images"
        :key="item.id"
        class="carousel-slide"
        :class="{ active: index === currentIndex }"
        :aria-hidden="index === currentIndex ? 'false' : 'true'"
      >
        <img :src="item.imageUrl" :alt="item.title || `招商内容第 ${index + 1} 页`" class="slide-image" />
        <div class="slide-shade"></div>
        <div class="slide-caption" v-if="item.title">
          <span>{{ String(index + 1).padStart(2, '0') }}</span>
          <strong>{{ item.title }}</strong>
        </div>
      </article>
    </div>

    <div class="investment-brand" v-if="images.length">
      <span>SHANGYA</span>
      <small>INVESTMENT & COOPERATION</small>
    </div>

    <button
      v-if="images.length > 1"
      class="carousel-arrow carousel-arrow--prev"
      type="button"
      aria-label="上一页"
      :disabled="currentIndex === 0"
      @click="prevPage"
    >
      <i class="el-icon-arrow-left"></i>
    </button>
    <button
      v-if="images.length > 1"
      class="carousel-arrow carousel-arrow--next"
      type="button"
      aria-label="下一页"
      :disabled="currentIndex === images.length - 1"
      @click="nextPage"
    >
      <i class="el-icon-arrow-right"></i>
    </button>

    <div class="carousel-dots" v-if="images.length > 1" role="tablist" aria-label="选择招商内容页">
      <button
        v-for="(item, index) in images"
        :key="item.id"
        class="dot"
        :class="{ active: index === currentIndex }"
        type="button"
        role="tab"
        :aria-selected="index === currentIndex ? 'true' : 'false'"
        :aria-label="`切换到第 ${index + 1} 页${item.title ? '：' + item.title : ''}`"
        @click="goTo(index)"
      ></button>
    </div>

    <div class="page-info" v-if="images.length > 0">
      <strong>{{ String(currentIndex + 1).padStart(2, "0") }}</strong>
      <span></span>
      <small>{{ String(images.length).padStart(2, "0") }}</small>
    </div>

    <div class="interaction-tip" v-if="images.length > 1">
      <i class="el-icon-sort"></i>
      <span>滚轮、方向键或滑动切换</span>
    </div>

    <div class="empty-state" v-if="!loading && images.length === 0">
      <i class="el-icon-picture-outline"></i>
      <strong>暂无招商内容</strong>
      <p>内容正在准备中，敬请期待。</p>
      <router-link to="/goin">了解尚亚科技</router-link>
    </div>

    <div class="loading-mask" v-if="loading">
      <span class="loading-ring"></span>
      <p>正在加载招商内容</p>
    </div>
  </section>
</template>

<script>
export default {
  name: "Investment",
  data() {
    return {
      images: [],
      currentIndex: 0,
      loading: true,
      wheelAccumulator: 0,
      wheelTimer: null,
      touchStartX: 0,
      touchStartY: 0
    };
  },
  computed: {
    trackStyle() {
      return {
        transform: `translateX(-${this.currentIndex * 100}%)`,
        transition: "transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)"
      };
    }
  },
  mounted() {
    this.loadImages();
  },
  beforeDestroy() {
    if (this.wheelTimer) clearTimeout(this.wheelTimer);
  },
  methods: {
    async loadImages() {
      this.loading = true;
      try {
        const response = await fetch("/api/investment");
        if (!response.ok) throw new Error("加载失败");
        const data = await response.json();
        this.images = Array.isArray(data) ? data.filter(item => item && item.imageUrl) : [];
      } catch (e) {
        window.console.error("加载招商图片失败：", e);
        this.images = [];
      } finally {
        this.loading = false;
      }
    },
    onWheel(event) {
      this.wheelAccumulator += event.deltaY;
      if (this.wheelTimer) clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => {
        if (Math.abs(this.wheelAccumulator) > 50) {
          this.wheelAccumulator > 0 ? this.nextPage() : this.prevPage();
        }
        this.wheelAccumulator = 0;
      }, 80);
    },
    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) {
        deltaX < 0 ? this.nextPage() : this.prevPage();
      }
    },
    nextPage() {
      if (this.currentIndex < this.images.length - 1) {
        this.currentIndex++;
      }
    },
    prevPage() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    goTo(index) {
      if (!this.images.length) return;
      this.currentIndex = Math.max(0, Math.min(index, this.images.length - 1));
    }
  }
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.investment-page {
  position: relative;
  width: 100%;
  height: calc(100vh - 72px);
  min-height: 560px;
  overflow: hidden;
  color: #fff;
  background: var(--brand-950);
  outline: none;
}

.carousel-track {
  display: flex;
  width: 100%;
  height: 100%;
  will-change: transform;
}

.carousel-slide {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  overflow: hidden;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #061722;
  transition: transform 1.2s var(--ease-out), filter 0.6s ease;
}

.carousel-slide.active .slide-image {
  transform: scale(1.01);
}

.slide-shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, var(--veil-mid), transparent 32%, transparent 72%, var(--veil-soft)),
    linear-gradient(180deg, var(--veil-soft), transparent 24%, transparent 70%, var(--veil-mid));
}

.slide-caption {
  position: absolute;
  bottom: 58px;
  left: clamp(76px, 8vw, 130px);
  display: flex;
  max-width: min(560px, 68vw);
  align-items: center;
  gap: 18px;
  text-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
}

.slide-caption span {
  color: var(--accent-300);
  font-family: Georgia, serif;
  font-size: 13px;
}

.slide-caption strong {
  font-size: clamp(18px, 2.2vw, 32px);
  font-weight: 500;
  line-height: 1.4;
}

.investment-brand {
  position: absolute;
  top: 32px;
  left: clamp(28px, 4vw, 64px);
  display: flex;
  flex-direction: column;
  text-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
}

.investment-brand span {
  font-family: Georgia, serif;
  font-size: 18px;
  letter-spacing: 0.32em;
}

.investment-brand small {
  margin-top: 5px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 7px;
  letter-spacing: 0.18em;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  z-index: 3;
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  border: 1px solid var(--glass-border);
  border-radius: 50%;
  color: #fff;
  background: var(--veil-mid);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transform: translateY(-50%);
  transition: background 0.25s ease, transform 0.25s ease, opacity 0.25s ease;
}

.carousel-arrow:hover:not(:disabled) {
  background: var(--brand-600);
  transform: translateY(-50%) scale(1.06);
}

.carousel-arrow:disabled {
  opacity: 0.24;
  cursor: default;
}

.carousel-arrow--prev {
  left: 28px;
}

.carousel-arrow--next {
  right: 28px;
}

.carousel-dots {
  position: absolute;
  right: 34px;
  bottom: 38px;
  z-index: 3;
  display: flex;
  max-width: 42vw;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.dot {
  width: 18px;
  height: 3px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  opacity: 0.52;
  background: #fff;
  cursor: pointer;
  transition: width 0.3s var(--ease-out), opacity 0.25s ease, background 0.25s ease;
}

.dot.active {
  width: 34px;
  opacity: 1;
  background: var(--accent-300);
}

.page-info {
  position: absolute;
  top: 30px;
  right: 38px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: Georgia, serif;
}

.page-info strong {
  font-size: 22px;
  font-weight: 400;
}

.page-info span {
  width: 34px;
  height: 1px;
  background: rgba(255, 255, 255, 0.52);
}

.page-info small {
  color: rgba(255, 255, 255, 0.56);
  font-size: 11px;
}

.interaction-tip {
  position: absolute;
  bottom: 36px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.56);
  font-size: 11px;
  letter-spacing: 0.08em;
  transform: translateX(-50%);
}

.interaction-tip i {
  transform: rotate(90deg);
}

.empty-state,
.loading-mask {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.78);
  background:
    radial-gradient(circle at 50% 42%, rgba(31, 145, 199, 0.18), transparent 34%),
    var(--brand-950);
}

.empty-state > i {
  margin-bottom: 18px;
  color: var(--accent-300);
  font-size: 48px;
}

.empty-state strong {
  color: #fff;
  font-size: 24px;
}

.empty-state p {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.54);
  font-size: 14px;
}

.empty-state a {
  margin-top: 28px;
  padding: 11px 20px;
  border: 1px solid var(--glass-border);
  border-radius: 999px;
  color: #fff;
  text-decoration: none;
}

.loading-ring {
  width: 38px;
  height: 38px;
  margin-bottom: 16px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: var(--accent-300);
  border-radius: 50%;
  animation: investment-spin 0.8s linear infinite;
}

.loading-mask p {
  font-size: 13px;
  letter-spacing: 0.12em;
}

@keyframes investment-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .investment-page {
    height: calc(100vh - var(--mobile-header-height));
    min-height: 500px;
  }

  .investment-brand {
    top: 22px;
    left: 18px;
  }

  .investment-brand span {
    font-size: 14px;
  }

  .page-info {
    top: 22px;
    right: 18px;
  }

  .page-info strong {
    font-size: 18px;
  }

  .carousel-arrow {
    top: auto;
    bottom: 74px;
    width: 44px;
    height: 44px;
    transform: none;
  }

  .carousel-arrow:hover:not(:disabled) {
    transform: none;
  }

  .carousel-arrow--prev {
    left: 18px;
  }

  .carousel-arrow--next {
    right: 18px;
  }

  .slide-caption {
    right: 18px;
    bottom: 138px;
    left: 18px;
    max-width: none;
  }

  .carousel-dots {
    right: 76px;
    bottom: 94px;
    left: 76px;
    max-width: none;
    justify-content: center;
  }

  .dot {
    width: 10px;
  }

  .dot.active {
    width: 22px;
  }

  .interaction-tip {
    bottom: 32px;
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .slide-image,
  .carousel-slide.active .slide-image {
    transition: none;
    transform: none;
  }

  .loading-ring {
    animation-duration: 1.5s;
  }
}
</style>
